/* @jsx h */
import { defineComponent, h } from "vue";
import { Dropdown } from "@lemmonhq/uikit";
import {
  KIND_META,
  PLATFORM_META,
  WALLET_PLATFORMS,
  type WalletPassDesign,
} from "../types";
import { WalletPassPreview } from "./WalletPassPreview";

interface WalletDesignCardProps {
  design: WalletPassDesign;
  canManage?: boolean;
  onEdit: (design: WalletPassDesign) => void;
  onDuplicate: (design: WalletPassDesign) => void;
  onDelete: (design: WalletPassDesign) => void;
}

export const WalletDesignCard = defineComponent<WalletDesignCardProps>({
  name: "WalletDesignCard",
  props: [
    "design",
    "canManage",
    "onEdit",
    "onDuplicate",
    "onDelete",
  ],
  setup(props) {
    return () => {
      const { design } = props;
      const meta = KIND_META[design.kind];

      return (
        <div class="flex flex-col rounded-xl border border-base-content/10 bg-base-100 overflow-hidden hover:shadow-md transition-shadow">
          {/* Mini preview — fixed height so cards line up across the grid,
              since an event ticket is shorter than a store card. */}
          <button
            class="p-5 bg-base-200/50 flex justify-center items-start cursor-pointer h-[300px] overflow-hidden"
            onClick={() => props.canManage !== false && props.onEdit(design)}
          >
            <div class="origin-top" style={{ transform: "scale(0.78)" }}>
              <WalletPassPreview
                design={design}
                target={
                  design.document.apple.poster.enabled ? "poster" : "legacy"
                }
              />
            </div>
          </button>

          <div class="p-4 flex flex-col gap-3 border-t border-base-content/10 flex-1">
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <h3 class="font-semibold text-sm truncate">{design.name}</h3>
                <div class="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span class="badge badge-ghost badge-sm gap-1">
                    <span class="material-symbols-outlined text-[13px]">
                      {meta.icon}
                    </span>
                    {meta.label}
                  </span>
                  {design.program_count > 0 ? (
                    <span class="badge badge-primary badge-sm">
                      Used by {design.program_count}{" "}
                      {design.program_count === 1 ? "program" : "programs"}
                    </span>
                  ) : (
                    <span class="badge badge-ghost badge-sm">Draft</span>
                  )}
                  {design.document.apple.poster.enabled && (
                    <span class="badge badge-outline badge-sm">iOS 27</span>
                  )}
                  {design.document.stamps?.enabled && (
                    <span class="badge badge-outline badge-sm gap-1">
                      <span class="material-symbols-outlined text-[13px]">
                        toll
                      </span>
                      Stamps
                    </span>
                  )}
                </div>
              </div>

              {props.canManage !== false && (
                <Dropdown
                  placement="bottom-end"
                  offset={8}
                  closeOnContentClick
                  anchorClass="shrink-0"
                  contentClass="menu menu-sm w-48 rounded-xl border border-base-content/10 bg-base-300 p-1.5 shadow-xl"
                >
                  {{
                    trigger: () => (
                      <button class="btn btn-ghost btn-xs">
                        <span class="material-symbols-outlined text-base">
                          more_vert
                        </span>
                      </button>
                    ),
                    default: () => (
                      <>
                        <li>
                          <a onClick={() => props.onEdit(design)}>Edit</a>
                        </li>
                        <li>
                          <a onClick={() => props.onDuplicate(design)}>
                            Duplicate
                          </a>
                        </li>
                        {/* A design a program selects cannot be deleted. */}
                        <li class={design.program_count > 0 ? "disabled" : ""}>
                          <a
                            class="text-error"
                            title={
                              design.program_count > 0
                                ? "Used by a program — choose another design for it first"
                                : undefined
                            }
                            onClick={() =>
                              design.program_count === 0 &&
                              props.onDelete(design)
                            }
                          >
                            Delete
                          </a>
                        </li>
                      </>
                    ),
                  }}
                </Dropdown>
              )}
            </div>

            <div class="flex items-center justify-between gap-2">
              <span class="flex items-center gap-1.5 text-base-content/50">
                {WALLET_PLATFORMS.filter((p) => design.document[p].enabled).map((p) => (
                  <span
                    key={p}
                    class="material-symbols-outlined text-[15px]"
                    title={PLATFORM_META[p].label}
                  >
                    {PLATFORM_META[p].icon}
                  </span>
                ))}
                <span class="text-[11px] font-mono">rev {design.revision}</span>
              </span>
              <button
                class="btn btn-xs btn-outline"
                disabled={props.canManage === false}
                onClick={() => props.onEdit(design)}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      );
    };
  },
});
